import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
  Dimensions,
  Modal,
  TextInput,
  FlatList,
  ActivityIndicator,
  Platform
  } from 'react-native';
  import { useApiWithLoading } from '../hooks/useApiWithLoading';
import { useSessionStore } from "../../../state/src/useSessionStore";
import type { SessionState } from "../../../state/src/useSessionStore";
// import { useSessionStore } from "../../../apps/web/src/stores/useSessionStore"; // local alias
// import { useNavigation } from "@react-navigation/native";
import i18n from "i18next";
import { useTranslation } from 'react-i18next';
import { loadCountries, getLocationDetailsByKID, postFeeLookup } from '../services/common.service';
import { ProductType } from '../view-models/productType';
import { ItemChoiceType } from '../view-models/itemChoiceType';
import { scale, moderateScale, verticalScale } from '../constants/scaling';
import Colors from '../constants/colors';
import GradientBackground from './uiComponents/GradientBackground';
import Icon from "../../../../assets/images/icon.png";
import LocationIcon from "../../../../assets/images/chevron.right.png";
import BillPayIcon from "../../../../assets/images/bill.png";
import SendIcon from "../../../../assets/images/send.png";
import CheckIcon from "../../../../assets/images/check.png";
import CVSLogo from "../../../../assets/images/cvs-logo-2.png";
import UpDownIcon from "../../../../assets/images/arr.chevron.up.down.png";
import EnglishFlag from "../../../../assets/images/english-flag.png";
import SpanishFlag from "../../../../assets/images/spanish-flag.png";
import MoneygramLogo from "../../../../assets/images/logo-moneygram-color.png";
import { Console } from 'winston/lib/winston/transports';
import { stringify } from 'querystring';
import appsettings from '../config/appsettings.json';
import { getPartnerHierarchy } from '../services/common.service';
import GlobalLoader from './uiComponents/GlobalLoader';
import { useGlobalError } from './uiComponents/GlobalErrorProvider';
const { width } = Dimensions.get('window');

interface DashboardProps {
  onLanguageChange?: (language: 'english' | 'spanish') => void;
  onServicePress?: (service: 'pickup' | 'send' | 'pay') => void;
  onGetStarted?: () => void;
}

type Country = {
  id: number;
  CountryCode: string;
  CountryName: string;
  IsActive: boolean;
};

export const Dashboard: React.FC<DashboardProps> = ({
  onLanguageChange,
  onServicePress,
  onGetStarted,
}) => {
  const { executeWithLoading } = useApiWithLoading();
  const isLoading = useSessionStore((s: SessionState) => (s as any).isLoading);
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'spanish'>('english');
  // const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [sendAmount, setSendAmount] = useState('100');
  const [query, setQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { showError } = useGlobalError();
  const [countries, setCountries] = useState<Array<{
    toLowerCase: any; id: number; CountryCode: string; CountryName: string; IsActive: boolean 
  }>>([]);
  
  const [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState<boolean>(false);
  const [isFeeLookupLoading, setIsFeeLookupLoading] = useState<boolean>(false);
  const [kid, setKid] = useState<string | null>(null);
  const { t } = useTranslation();
  const [locationDetails, setLocationDetails] = useState<{
    AgentId?: string | null;
    UnitProfileId?: string | null;
    LocationName?: string | null;
  } | null>(null);
  const lastFeeLookupKeyRef = useRef<string | null>(null);

  // Partner hierarchy details
  const [partnerDetails, setPartnerDetails] = useState<{ name: string; address: string } | null>(null);
  const partnerName = partnerDetails?.name || '';
  const partnerAddress = partnerDetails?.address || '';

  // Store Required Data into zustand
  const setIsReceiver = useSessionStore((s: SessionState) => (s as any).setIsReceiver);
  const setIsSendCash = useSessionStore((s: SessionState) => (s as any).setIsSendCash);
  const setIsBillPay = useSessionStore((s: SessionState) => (s as any).setIsBillPay);

  const setAgentId = useSessionStore((s: SessionState) => s.setAgentId);
  const setUnitProfileId = useSessionStore((s: SessionState) => s.setUnitProfileId);
  const setKioskId = useSessionStore((s: SessionState) => (s as any).setKioskId);
  const setLocationNameStore = useSessionStore((s: SessionState) => (s as any).setLocationName);
  // const navigation = useNavigation<any>();

  // Add state for exchange rate information
  const [exchangeRateInfo, setExchangeRateInfo] = useState<{
    exchangeRate: string;
    receiveCurrency: string;
    isEstimated: boolean;
    totalSendFees: number;
  } | null>(null);

  // Dynamic flag mapping - you can expand this as needed
  const getFlagForCode = (code: string): string => {
    const flagMap: Record<string, string> = {
      MEX: '🇲🇽',
      PHL: '🇵🇭',
      JAM: '🇯🇲',
      ARM: '🇦🇲',
      IND: '🇮🇳',
    };
    return flagMap[code] || '🌐';
  };

  // filter countries (fetched from API)
  const filteredCountries: Country[] = useMemo(() => {
    const list = (countries as Country[]).filter((c) => !!c);
    const normalizedQuery = search.toLowerCase().trim();
    const toName = (c: Country) => (typeof c?.CountryName === 'string' ? c.CountryName : '').trim();
    const base = normalizedQuery
      ? list.filter((c) => toName(c).toLowerCase().includes(normalizedQuery))
      : [...list].sort((a, b) => toName(a).localeCompare(toName(b)));
    return base.slice(0, 5);
  }, [countries, search]);

  // Fetch countries only after we have a valid kiosk token (kid)
useEffect(() => {
  const fetchCountries = async () => {
    console.log('🔄 Fetching countries from service...');
    try {
      await executeWithLoading(async () => {
        const result = await loadCountries();
        console.log('📡 Service Response:', result);

        // The error is because the state expects objects with a 'toLowerCase' property, but Country does not have it.
        // To fix, add a dummy toLowerCase property to each country object.
        const apiCountries: Country[] = result?.data ?? [];
        setCountries(
          apiCountries.map((c) => ({
            ...c,
            toLowerCase: undefined, // or you can use: () => c.CountryName?.toLowerCase?.() ?? ''
          }))
        );

        if (apiCountries.length > 0) {
          const defaultCountry = apiCountries.find(
            (country) => country.CountryCode === "MEX"
          );

          if (defaultCountry) {
            setSelectedCountry(defaultCountry.CountryName);
            setSelectedCountryCode(defaultCountry.CountryCode);
            console.log(
              "Default country set to:",
              defaultCountry.CountryName,
              defaultCountry.CountryCode
            );
          } else {
            // fallback: set first country if India is not found
            setSelectedCountry(apiCountries[0].CountryName);
            setSelectedCountryCode(apiCountries[0].CountryCode);
            console.log(
              "Default country fallback set to:",
              apiCountries[0].CountryName,
              apiCountries[0].CountryCode
            );
          }
        }
      }, "Loading countries...");
    } catch (error) {
      console.error('💥 Failed to fetch countries:', error);
      setCountriesError('Failed to load countries');
    }
  };

  if (!kid) {
    return;
  }

  fetchCountries();
}, [kid]);

// Fetch location details when KID is available
useEffect(() => {
  const fetchLocationDetails = async () => {
    if (kid) {
      try {
        await executeWithLoading(async () => {
          const result = await getLocationDetailsByKID(kid); //{data: {AgentId: "66024839", UnitProfileId: "2668580"}};//
          console.log("LOCATIONS DETAILS==>", result)
          if(result?.data?.Status?.toLowerCase() != 'active') {
            console.log('Location statu is ' + result?.data?.Status);
            showError('Location status is ' + result?.data?.Status);
            return;
          }
          if (result?.data) {
            setLocationDetails(result.data);

            if (result.data.AgentId) {
              await fetchPartnerHierarchy(result.data.AgentId);
            }

            // if (selectedCountryCode && selectedCountry) {
            //   performFeeLookup(selectedCountryCode, selectedCountry);
            // }
          } else {
            console.log('No location details found for KID:', kid);
            setLocationDetails(null);
          }
        }, "Loading location...");
      } catch (error) {
        console.error('💥 Failed to fetch location details for KID:', error);
        setLocationDetails(null);
      }
    }
  };

  fetchLocationDetails();
}, [kid, selectedCountryCode, selectedCountry]);

const fetchPartnerHierarchy = async (agentId: string) => {
  try {
    await executeWithLoading(async () => {
      const payload = await getPartnerHierarchy(agentId);
      const name: string = (payload as any)?.name ?? '';
      const address: string = (payload as any)?.address ?? '';
      setPartnerDetails({ name, address });
      setLocationDetails((prev) => ({ ...(prev || {}), LocationName: name }));
      try { setLocationNameStore?.(name); } catch {}
      console.log('PartnerHierarchy details:', { name, address });
    }, "Loading partner details...");
  } catch (error) {
    console.error('Failed to fetch PartnerHierarchy:', error);
  }
};

  const handleSelect = (country: Country) => {
    console.log('🌍 Country selected:', country);
    setSelectedCountry(country.CountryName);
    setSelectedCountryCode(country.CountryCode);
    setIsCountryDropdownOpen(false);
    // Perform fee lookup when country is selected
    performFeeLookup(country.CountryCode, country.CountryName);
    // setSelectedCountry(country);
    setDropdownOpen(false);
    setSearch("");
  };

  const handleLanguageChange = (language: 'english' | 'spanish') => {
    if(language === 'english') {
        i18n.changeLanguage("en")
    } else {
        i18n.changeLanguage("sp")
    }
    setSelectedLanguage(language);
    onLanguageChange?.(language);
  };

  const handleServicePress = (service: 'pickup' | 'send' | 'pay') => {
    // Prevent interactions during initial/ongoing API calls
    if (isLoading) {
      return;
    }

    if(service === 'pickup') {
      try {
        setIsReceiver?.(true);
        setIsSendCash?.(false);
        setIsBillPay?.(false); 
      } catch {}
    } else if(service === 'send') {
      try {
        setIsReceiver?.(false);
        setIsSendCash?.(true);
        setIsBillPay?.(false);  } catch {}
    } else if (service === 'pay') {
      try {
        setIsReceiver?.(false);
        setIsSendCash?.(false);
        setIsBillPay?.(true);  } catch {}
    }
    onServicePress?.(service);
    setAgentId(locationDetails?.AgentId ?? null);
    setUnitProfileId(locationDetails?.UnitProfileId ?? null);
    // Navigate
    // if (service === "send") {
    //   navigation.navigate("SendScreen");
    // } else if (service === "pickup") {
    //   navigation.navigate("PickupScreen");
    // } else if (service === "pay") {
    //   navigation.navigate("BillPayScreen");
    // }

    // Still call optional prop callback
    onServicePress?.(service);
  };

  // const handleGetStarted = () => {
  //   onGetStarted?.();
  // };

const performFeeLookup = async (countryCode: string, countryName: string) => {
  await executeWithLoading(async () => {
    if (!locationDetails?.AgentId || !locationDetails?.UnitProfileId) {
      console.log('Cannot perform fee lookup: Missing AgentId or UnitProfileId');
      return;
    }

    console.log('Performing fee lookup for country:', countryCode, countryName);
    const feeLookupRequest = {
      agentID: locationDetails.AgentId,
      unitProfileID: locationDetails.UnitProfileId,
      amount: parseFloat(sendAmount.replace(/,/g, '')),
      sendCurrency: 'USD',
      destinationCountry: countryCode,
      allOptions: true,
      productType: ProductType.SEND,
      amountExcludingFee: 1,
    };

    const result = await postFeeLookup(feeLookupRequest);
    console.log('Fee lookup response:', result);

    if (result?.exchangeRates?.length > 0) {
      console.log('result?.exchangeRates', result?.exchangeRates)
      const firstExchangeRate = result.exchangeRates[0];
      setExchangeRateInfo({
        exchangeRate: firstExchangeRate.exchangeRate?.toString() || '0',
        receiveCurrency: firstExchangeRate.receiveCurrency || '',
        isEstimated: firstExchangeRate.isEstimated || false,
        totalSendFees: firstExchangeRate.totalSendFees || 0,
      });
    } else {
      setExchangeRateInfo(null);
    }
  }, "Fetching rates & fees...");
};

  // Read KID from URL query parameters on mount
  useEffect(() => {
    const readKidFromUrl = () => {
      try {
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
          console.log('KIDDDDDD==>', window.location.search)
          const urlParams = new URLSearchParams(window.location.search);
          const kidParam = urlParams.get('t');
          console.log('kidParam==>', kidParam)
          if (kidParam) {
            console.log('📋 kiosk token found in URL:', kidParam);
            setKid(kidParam);
            try { setKioskId?.(kidParam); } catch {}
          } else {
            console.log('📋 No kiosk token found in URL');
            showError('QR Code details are missing. Please scan the QR code again');
          }
        }
      } catch (error) {
        console.error('💥 Error reading KID from URL:', error);
      }
    };

    readKidFromUrl();
  }, []);

  useEffect(() => {
    const agentId = locationDetails?.AgentId;
    const unitId = locationDetails?.UnitProfileId;
    if (selectedCountryCode && selectedCountry && agentId && unitId) {
      const key = `${agentId}|${unitId}|${selectedCountryCode}|${selectedCountry}`;
      if (lastFeeLookupKeyRef.current === key) {
        return; // prevent duplicate calls (StrictMode/effect refires or unrelated location updates)
      }
      lastFeeLookupKeyRef.current = key;
      performFeeLookup(selectedCountryCode, selectedCountry);
    }
  }, [selectedCountryCode, selectedCountry, locationDetails?.AgentId, locationDetails?.UnitProfileId]);

  const parseAmount = (text: string): string => {
    // allow only digits and one optional decimal point
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false} contentContainerStyle={styles.contentContainer}>
      <View style={styles.locationInfo}>
        <View style={{ flexDirection: 'row' }}>
          <Image
              source={LocationIcon}
              style={{height: 24, width: 24, marginRight: 8}}        
            />
            {/* Restricting address to be for first 20 characters */}
          <Text numberOfLines={1} ellipsizeMode="tail" style={styles.locationInfoText}>
            {partnerDetails?.address && partnerDetails.address.length > 20
              ? partnerDetails.address.slice(0, 20) + '...'
              : partnerDetails?.address || 'N/A'}
          </Text>
        </View>
        <Text style={styles.locationAddressText}>{partnerDetails?.name || 'N/A'}</Text>
      </View>
      
      {/* Language Selection */}
      <View style={styles.languageSection}>
        <Text style={styles.languageTitle}>Select language</Text>
        <View style={styles.languageButtons}>
          <TouchableOpacity
            style={[
              styles.languageButton,
              selectedLanguage === 'english' && styles.languageButtonSelected,
              { paddingRight: 4}
            ]}
            onPress={() => handleLanguageChange('english')}
          >
            <Image
              source={EnglishFlag}
              style={styles.flagContainer}
              resizeMode='contain'           
            />
            <Text style={[
              styles.languageText,
              selectedLanguage === 'english' && styles.languageTextSelected,
            ]}>
              English
            </Text>
            {selectedLanguage === 'english' && (
              <Image
              source={CheckIcon}
              style={{height: 16, width: 16, marginRight: 16}}
              resizeMode='contain'           
            />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.languageButton,
              selectedLanguage === 'spanish' && styles.languageButtonSelected,
            ]}
            onPress={() => handleLanguageChange('spanish')}
          >
              <Image
              source={SpanishFlag}
              style={styles.flagContainer}
              resizeMode='contain'           
            />
            <Text style={[
              styles.languageText,
              selectedLanguage === 'spanish' && styles.languageTextSelected,
            ]}>
              Spanish
            </Text>
            {selectedLanguage === 'spanish' && (
              <Image
                source={CheckIcon}
                style={{height: 16, width: 16, marginRight: 8}}
                resizeMode='contain'      
              />
            )}
          </TouchableOpacity>
        </View>
      </View>
   
        {/* CVS Logo and Slogan */}
        <View style={styles.brandSection}>
          <Image
              source={CVSLogo}
              style={{height: 40, width: 160}}
              resizeMode='contain'
          />
          <Text style={styles.slogan}>{t('SLOGAN')}</Text>
        </View>

        {/* Core Services */}
        <View style={styles.servicesSection}>
          <TouchableOpacity
            style={[styles.serviceButton, (isLoading || !kid) ? styles.disabledButton : undefined]}
            disabled={isLoading || !kid}
            onPress={() => handleServicePress('pickup')}
          >
            <View>
              <Image
                source={Icon}
                style={styles.serviceIcon}       
              />
              <Text style={styles.serviceText}>
                {/* {t('PICK_UP_CASH')} */}
                {t('PICK_UP')}{"\n"}
                {t('CASH')}
                </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.serviceButton, {marginHorizontal: scale(16)}, (isLoading || !kid) ? styles.disabledButton : undefined]}
            disabled={isLoading || !kid}
            onPress={() => handleServicePress('send')}
          >
            <Image
              source={SendIcon}
              style={styles.serviceIcon}        
            />
            <Text style={styles.serviceText}>
              {/* {t('SEND_CASH')} */}
              {t('SEND')}{"\n"}
              {t('CASH')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.serviceButton, (isLoading || !kid) ? styles.disabledButton : undefined]}
            disabled={isLoading || !kid}
            onPress={() => handleServicePress('pay')}
          >
            <Image
              source={BillPayIcon}
              style={styles.serviceIcon}        
            />
            <Text style={styles.serviceText}>
              {t('PAY')}{"\n"}
              {t('BILLS')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Rates & Fees Section */}
        <View style={styles.ratesSection}>
          <Text style={styles.ratesTitle}>{t('EXPLORE_RATE_AND_FEES')}</Text>

          {/* You're sending to */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{t('YOU_ARE_SENDING_TO')}</Text>
            <View style={styles.countrySelectWrapper}>
              {/* Dropdown Button */}
              <TouchableOpacity style={styles.countrySelector} onPress={() => setDropdownOpen((prev) => !prev)}>
                <Text style={styles.flag}>{selectedCountryCode ? getFlagForCode(selectedCountryCode) : '🌐'}</Text>
                <Text style={styles.countryText}>{selectedCountry}</Text>
                <Image
                  source={UpDownIcon}
                  style={styles.serviceIcon}        
                />
              </TouchableOpacity>
              <View style={styles.countryContainer}>
                  {/* Dropdown List */}
                  {isDropdownOpen && (
                    <View style={styles.dropdownOpen}>
                      {/* Search Input */}
                      <TextInput
                        style={styles.searchInput}
                        placeholder="Search country..."
                        value={search}
                        onChangeText={setSearch}
                      />

                      {/* Country List */}
                      <FlatList
                        data={filteredCountries}
                        keyExtractor={(item) => item?.CountryCode || String(item?.id) || item?.CountryName}
                        renderItem={({ item }) => (
                          <TouchableOpacity
                            style={styles.countryItem}
                            onPress={() => handleSelect(item)}
                          >
                            <Text style={styles.countryText}>{item.CountryName}</Text>
                          </TouchableOpacity>
                        )}
                      />
                    </View>
                  )}
                </View>
            </View>
            <Text style={styles.exchangeRate}>
              {exchangeRateInfo ? 
                `1 USD = ${exchangeRateInfo.exchangeRate} ${exchangeRateInfo.receiveCurrency}` : ''
              }
            </Text>
            {isFeeLookupLoading && (
              <Text style={styles.helperText}>🔄 {t('FETCHING_FEES')}</Text>
            )}
            {isCountriesLoading && (
              <Text style={styles.helperText}>{t('LOADING_COUNTRIES')}</Text>
            )}
            {!!countriesError && (
              <Text style={styles.errorText}>{countriesError}</Text>
            )}
          </View>

          {/* You send */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{t('YOU_SEND')}</Text>
            <View style={styles.amountInput}>
              <TextInput
                style={styles.amountText}
                keyboardType="decimal-pad"
                placeholder="0.00"
                value={sendAmount}
                onChangeText={(t) => {
                  const next = parseAmount(t);
                  setSendAmount(next);
                }}
              />
              <Text style={styles.currencyText}>USD</Text>
            </View>
          </View>

          {/* They receive */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{t('THEY_RECEIVE')}</Text>
            <View style={[styles.amountInput]}>
              <Text style={[styles.amountText, styles.amountTextGreen]}>
                {exchangeRateInfo ? 
                  (() => {
                    const numericAmount = parseFloat(sendAmount.replace(/,/g, ''));
                    const received = numericAmount * parseFloat(exchangeRateInfo.exchangeRate);
                    return `${received.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  })() : 
                  '0.00'
                }
              </Text>
              <Text style={[styles.currencyText, { color: Colors.black}]}>
                {exchangeRateInfo?.receiveCurrency || ''}
              </Text>
            </View>
          </View>

          
        </View>
        {/* Fees and CTA */}
        <View style={{ marginHorizontal: 24}}>
          <GradientBackground style={styles.card}>
            <View style={styles.row}>
              <Text style={styles.feeLabel}>Fees</Text>
              <View style={{ flexDirection: 'row'}}>
                <Text style={styles.feeLabel}>{exchangeRateInfo?.totalSendFees || 0} USD</Text>
              </View>
            </View>
            <TouchableOpacity
              style={[styles.button, (isLoading || !kid) ? styles.disabledButton : undefined]}
              disabled={isLoading || !kid}
              onPress={() => handleServicePress('send')}
            >
              <Text style={styles.buttonText}>{t("GET_STARTED")}</Text>
            </TouchableOpacity>
          </GradientBackground>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.moneygramLogo}>
            <Text style={styles.poweredBy}>{t("POWERED_BY")}</Text>
            <Image
              source={MoneygramLogo}
              style={{ height: 16, width: 70}}
              resizeMode='contain'    
            />
          </View>
        </View>
      </ScrollView>
    
  );
};

export default Dashboard;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 50,
  },
  locationInfo: {
    height: verticalScale(36),
    paddingHorizontal: scale(24),
    flexDirection: 'row',
    backgroundColor: Colors.black,
    justifyContent: 'space-between',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    alignItems: 'center',
  },
  locationInfoText: {
    // maxWidth: scale(120),
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: verticalScale(24),
    color: Colors.white,
  },
  locationAddressText: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: verticalScale(16),
    letterSpacing: 0.06,
    color: Colors.white,
  },
  languageSection: {
    marginHorizontal: 24,
    marginTop: 15,
  },
  languageTitle: {
    color: Colors.secondaryText,
    textAlign: 'center',
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: verticalScale(20),
    letterSpacing: -0.16,
  },
  languageButtons: {
    flexDirection: 'row',
    justifyContent: "space-evenly",
    gap: 10,
    borderWidth: 1,
    borderColor: Colors.borderGrey,
    borderRadius: 8,
    padding: 4,
    marginTop: 12,
    alignSelf: 'stretch',
  },
  languageButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.white,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
    justifyContent: 'center',
  },
  languageButtonSelected: {
    backgroundColor: Colors.black,
    borderWidth: 0,
  },
  flagContainer: {
    marginRight: 10,
    height: 28,
    width: 28,
    borderRadius: 50
  },
  flag: {
    fontSize: 20,
  },
  languageText: {
    color: '#1F1F1F',
    textAlign: 'center',
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: verticalScale(22),
    letterSpacing: 0.07,
    marginRight: 8
  },
  languageTextSelected: {
    color: '#fff',
  },
  checkmark: {
    fontSize: 18,
    color: '#4CAF50',
    marginLeft: 8,
  },
  brandSection: {
    alignItems: 'center',
    marginTop: 40
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 32,
  },
  heartIcon: {
    fontSize: 32,
    marginRight: 8,
  },
  cvsText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#e31837',
  },
  slogan: {
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    textAlign: 'center',
    lineHeight: 36,
    width: 330,
    paddingHorizontal: 20,
    marginTop: 32
  },
  servicesSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 40,
    marginHorizontal: 24,
  },
  serviceButton: {
    flex: 1,
    backgroundColor: '#F2F2F2',
    borderRadius: 20,
    padding: 16,
    alignItems: 'flex-start',
    justifyContent: 'space-evenly',
    borderColor:'#CACACA',
    borderWidth: 1,
     // iOS shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    // Android shadow
    elevation: 8,     
  },
  disabledButton: {
    opacity: 0.5,
  },
  serviceIcon: {
    height: 24,
    width: 24,
    marginBottom: 8,
  },
  serviceText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.secondaryText,
    maxWidth: scale(67),
    lineHeight: 24,
    alignSelf: 'stretch'
  },
  ratesSection: {
    backgroundColor: '#fff',
    marginHorizontal: 24,
    borderTopEndRadius: 12,
    borderTopRightRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  ratesTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: '500',
    color: '#1F1F1F',
    marginBottom: 24,
    textAlign: 'center',
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: '#1F1F1F',
    marginBottom: 4,
  },
  countrySelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginBottom: 8,
  },
  countrySelectWrapper: {
    position: 'relative',
  },
  countryText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 8,
    flex: 1,
  },
  helperText: {
    marginTop: 6,
    fontSize: 12,
    color: '#666',
  },
  errorText: {
    marginTop: 6,
    fontSize: 12,
    color: '#d32f2f',
  },
  exchangeRate: {
    fontFamily: 'Rubik',
    fontSize: 16,
    color: Colors.successInfoGreen,
    fontWeight: '400',
    alignSelf: 'center',
    lineHeight: 16
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  amountText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  amountTextGreen: {
    color: '#4CAF50',
  },
  currencyText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Rubik',
    color: Colors.darkGrey,
    alignSelf: 'center',
    lineHeight: 20
  },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  previousAmount: {
    fontSize: 14,
    color: '#999',
    marginBottom: 4,
  },
  receivedAmountContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  receivedAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4CAF50',
    flex: 1,
  },
  receivedCurrency: {
    fontSize: 16,
    color: '#666',
  },
  feesContainer: {
    borderRadius: 12,
    padding: 15,
    marginBottom: 20,
    backgroundColor: '#FFE4B5',
  },
  feesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  feesLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  feesAmount: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  originalFee: {
    fontSize: 14,
    color: '#666',
    textDecorationLine: 'line-through',
  },
  freeText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4CAF50',
  },
  getStartedButton: {
    backgroundColor: '#333',
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
  },
  getStartedText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 30,
    paddingHorizontal: 20,
  },
  poweredBy: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: '300',
    color: Colors.black,
    marginRight: 8
  },
  moneygramLogo: {
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'center'
  },
  text: {
    fontSize: 16,
  },
    countryContainer: {
    // margin: 20,
  },
  dropdownOpen: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    backgroundColor: "#fff",
    maxHeight: 250,
  },
  searchInput: {
    borderBottomWidth: 1,
    borderColor: "#eee",
    padding: 10,
    fontSize: 16,
  },
  countryItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: "#f0f0f0",
  },
  card: {
    alignItems: "center",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  feeLabel: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    color: Colors.titleTextColor,
    fontFamily: 'Rubik'
  },
  oldPrice: {
    fontSize: 16,
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: Colors.titleTextColor,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  buttonText: {
    color: Colors.buttonTextColor,
    fontWeight: "500",
    fontSize: 16,
    lineHeight: 20
  },
  powered: {
    fontSize: 12,
    color: "gray",
  },
});


